import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AdvancedFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  initialFilters?: FilterState;
}

export interface FilterState {
  priceRange: [number, number];
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  technique: string;
  color: string;
}

const TECHNIQUES = [
  "Alla tekniker",
  "Akryl",
  "Olja",
  "Akvarell",
  "Blandteknik",
  "Digital",
  "Pastell",
  "Gouache",
  "Tusch",
  "Kol",
  "Collage",
  "Grafik",
  "Foto"
];

const COLORS = [
  { value: "all", label: "Alla färger" },
  { value: "Röd", label: "Röd" },
  { value: "Blå", label: "Blå" },
  { value: "Grön", label: "Grön" },
  { value: "Gul", label: "Gul" },
  { value: "Orange", label: "Orange" },
  { value: "Lila", label: "Lila" },
  { value: "Rosa", label: "Rosa" },
  { value: "Brun", label: "Brun" },
  { value: "Svart", label: "Svart" },
  { value: "Vit", label: "Vit" },
  { value: "Grå", label: "Grå" },
  { value: "Flerfärgad", label: "Flerfärgad" }
];

export function AdvancedFilters({ onFiltersChange, initialFilters }: AdvancedFiltersProps) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(
    initialFilters || {
      priceRange: [0, 50000],
      technique: "all",
      color: "all"
    }
  );

  const handleApply = () => {
    onFiltersChange(filters);
    setOpen(false);
  };

  const handleReset = () => {
    const resetFilters: FilterState = {
      priceRange: [0, 50000],
      technique: "all",
      color: "all"
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const activeFiltersCount = () => {
    let count = 0;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 50000) count++;
    if (filters.minWidth || filters.maxWidth) count++;
    if (filters.minHeight || filters.maxHeight) count++;
    if (filters.technique !== "all") count++;
    if (filters.color !== "all") count++;
    return count;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          className="relative shrink-0 h-10 w-10 sm:h-12 sm:w-12"
        >
          <SlidersHorizontal className="h-4 w-4 sm:h-5 sm:w-5" />
          {activeFiltersCount() > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {activeFiltersCount()}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Avancerade filter</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Price Range */}
          <div className="space-y-3">
            <Label>Prisintervall</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {filters.priceRange[0].toLocaleString('sv-SE')} kr - {filters.priceRange[1].toLocaleString('sv-SE')} kr
            </div>
            <Slider
              value={filters.priceRange}
              onValueChange={(value) => setFilters({ ...filters, priceRange: value as [number, number] })}
              min={0}
              max={50000}
              step={500}
              className="w-full"
            />
          </div>

          {/* Width Range */}
          <div className="space-y-3">
            <Label>Bredd (cm)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Min</Label>
                <Select
                  value={filters.minWidth?.toString() || "all"}
                  onValueChange={(value) => 
                    setFilters({ ...filters, minWidth: value === "all" ? undefined : Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Ingen min</SelectItem>
                    <SelectItem value="20">20 cm</SelectItem>
                    <SelectItem value="30">30 cm</SelectItem>
                    <SelectItem value="40">40 cm</SelectItem>
                    <SelectItem value="50">50 cm</SelectItem>
                    <SelectItem value="70">70 cm</SelectItem>
                    <SelectItem value="100">100 cm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max</Label>
                <Select
                  value={filters.maxWidth?.toString() || "all"}
                  onValueChange={(value) => 
                    setFilters({ ...filters, maxWidth: value === "all" ? undefined : Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Max" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Ingen max</SelectItem>
                    <SelectItem value="30">30 cm</SelectItem>
                    <SelectItem value="40">40 cm</SelectItem>
                    <SelectItem value="50">50 cm</SelectItem>
                    <SelectItem value="70">70 cm</SelectItem>
                    <SelectItem value="100">100 cm</SelectItem>
                    <SelectItem value="150">150 cm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Height Range */}
          <div className="space-y-3">
            <Label>Höjd (cm)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Min</Label>
                <Select
                  value={filters.minHeight?.toString() || "all"}
                  onValueChange={(value) => 
                    setFilters({ ...filters, minHeight: value === "all" ? undefined : Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Ingen min</SelectItem>
                    <SelectItem value="20">20 cm</SelectItem>
                    <SelectItem value="30">30 cm</SelectItem>
                    <SelectItem value="40">40 cm</SelectItem>
                    <SelectItem value="50">50 cm</SelectItem>
                    <SelectItem value="70">70 cm</SelectItem>
                    <SelectItem value="100">100 cm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max</Label>
                <Select
                  value={filters.maxHeight?.toString() || "all"}
                  onValueChange={(value) => 
                    setFilters({ ...filters, maxHeight: value === "all" ? undefined : Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Max" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Ingen max</SelectItem>
                    <SelectItem value="30">30 cm</SelectItem>
                    <SelectItem value="40">40 cm</SelectItem>
                    <SelectItem value="50">50 cm</SelectItem>
                    <SelectItem value="70">70 cm</SelectItem>
                    <SelectItem value="100">100 cm</SelectItem>
                    <SelectItem value="150">150 cm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Technique */}
          <div className="space-y-3">
            <Label>Teknik</Label>
            <Select
              value={filters.technique}
              onValueChange={(value) => setFilters({ ...filters, technique: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Välj teknik" />
              </SelectTrigger>
              <SelectContent>
                {TECHNIQUES.map((tech) => (
                  <SelectItem 
                    key={tech} 
                    value={tech === "Alla tekniker" ? "all" : tech}
                  >
                    {tech}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Color */}
          <div className="space-y-3">
            <Label>Dominerande färg</Label>
            <Select
              value={filters.color}
              onValueChange={(value) => setFilters({ ...filters, color: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Välj färg" />
              </SelectTrigger>
              <SelectContent>
                {COLORS.map((color) => (
                  <SelectItem key={color.value} value={color.value}>
                    {color.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleReset}
            className="flex-1"
          >
            <X className="h-4 w-4 mr-2" />
            Rensa
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Tillämpa filter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}